import * as THREE from "three";
import type { Surface } from "../styles";
import { sampleCurve } from "../../prims/curve";
import type { ShopSign } from "../../prims/scenery";
import { inkFor, SIGN_H, SIGN_W, signTexture } from "../shop-signs";
import { inked, toon, type ToyParams } from "../toon";

// The building blocks every scenery mesh (#156) is made of. One `Kit` per
// park build hands out shared materials (so `mergeStatic` batches every part
// in one color into one draw) and places simple solids.

/** Something that moves on the render clock: the wheel turning, the carousel going round. */
export type Tick = (t: number) => void;

/** Marks `obj` to be moved every frame by `tick(t)`, `t` in seconds on the render clock. */
export function onTick(obj: THREE.Object3D, tick: Tick): void {
  obj.userData.tick = tick;
}

/** Every object under `root` that moves on the render clock. */
export function tickers(root: THREE.Object3D): Tick[] {
  const out: Tick[] = [];
  root.traverse((o) => {
    if (typeof o.userData.tick === "function") out.push(o.userData.tick as Tick);
  });
  return out;
}

const UP = new THREE.Vector3(0, 1, 0);

export class Kit {
  private geoms = new Map<string, THREE.BufferGeometry>();

  /**
   * With `shared`, parts reuse one geometry per shape (a unit box or ball,
   * scaled), which is only safe when everything built is batched by one
   * `mergeStatic`: it disposes each source once. Moving rides get a kit
   * without it. `mats` is where its materials are kept: pass one map to
   * several kits to share their materials across builds (#216).
   */
  constructor(
    private readonly shared = true,
    private readonly mats = new Map<string, THREE.Material>(),
  ) {}

  /** One geometry per `key` (see `shared`), made by `make` the first time. */
  geom(key: string, make: () => THREE.BufferGeometry): THREE.BufferGeometry {
    if (!this.shared) return make();
    let g = this.geoms.get(key);
    if (!g) this.geoms.set(key, (g = make()));
    return g;
  }

  /** A shared surface material in this color. */
  mat(color: THREE.ColorRepresentation, surface: Surface = "paint", extra: ToyParams = {}): THREE.Material {
    const key = `${new THREE.Color(color).getHexString()}:${surface}:${JSON.stringify(extra)}`;
    let m = this.mats.get(key);
    if (!m) this.mats.set(key, (m = toon(color, { roughness: 0.7, ...extra }, surface)));
    return m;
  }

  /** A shared unlit material: bulbs, lamps and water that should read bright whatever the light. */
  glow(color: THREE.ColorRepresentation): THREE.Material {
    const key = `glow:${new THREE.Color(color).getHexString()}`;
    let m = this.mats.get(key);
    if (!m) this.mats.set(key, (m = new THREE.MeshBasicMaterial({ color })));
    return m;
  }

  /** A shared see-through, unlit, additive material (#246): a light shaft's haze, brightening whatever is behind it. */
  haze(color: THREE.ColorRepresentation, opacity: number): THREE.Material {
    const key = `haze:${new THREE.Color(color).getHexString()}:${opacity}`;
    let m = this.mats.get(key);
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
      this.mats.set(key, m);
    }
    return m;
  }

  /**
   * A shop's name board (#233), `w` wide in the street signs' proportions,
   * its face looking along local +x with its middle at (x, y, z): a trim
   * frame and the lettered face on `bg`, lit a little from within like the
   * street's. Each sign has its own texture, so one never repeats another.
   */
  sign(parent: THREE.Object3D, sign: ShopSign, bg: string, w: number, x: number, y: number, z: number): void {
    const h = (w * SIGN_H) / SIGN_W;
    this.box(parent, 0.1, h + 0.16, w + 0.16, "#f4f1ea", x - 0.06, y - h / 2 - 0.08, z);
    const key = `sign:${sign.name}:${sign.line ?? ""}:${bg}`;
    let m = this.mats.get(key);
    if (!m) {
      const map = signTexture({ name: sign.name, line: sign.line ?? "", bg, ink: inkFor(bg), awning: bg });
      m = toon(map ? "#ffffff" : bg, map ? { map, emissive: "#ffffff", emissiveMap: map, emissiveIntensity: 0.3, roughness: 0.6 } : { roughness: 0.6 }, "paint");
      this.mats.set(key, m);
    }
    const face = new THREE.Mesh(new THREE.PlaneGeometry(w, h), m);
    face.position.set(x + 0.01, y, z);
    face.rotation.y = Math.PI / 2; // the plane faces +z; turn it to face +x
    parent.add(face);
  }

  /** A box `w` x `h` x `d` whose bottom middle sits at (x, y, z) in `parent`. */
  box(parent: THREE.Object3D, w: number, h: number, d: number, color: THREE.ColorRepresentation, x: number, y: number, z: number, surface: Surface = "paint"): THREE.Mesh {
    const m = inked(this.geom("box", () => new THREE.BoxGeometry(1, 1, 1)), this.mat(color, surface), 0.012);
    m.scale.set(w, h, d);
    m.position.set(x, y + h / 2, z);
    m.receiveShadow = true;
    parent.add(m);
    return m;
  }

  /** An upright cylinder (radii top and bottom) whose bottom middle sits at (x, y, z). */
  cyl(parent: THREE.Object3D, rt: number, rb: number, h: number, color: THREE.ColorRepresentation, x: number, y: number, z: number, segs = 12, surface: Surface = "paint"): THREE.Mesh {
    const m = inked(this.geom(`cyl:${rt}:${rb}:${h}:${segs}`, () => new THREE.CylinderGeometry(rt, rb, h, segs)), this.mat(color, surface), 0.012);
    m.position.set(x, y + h / 2, z);
    m.receiveShadow = true;
    parent.add(m);
    return m;
  }

  /** A sphere centred at (x, y, z). */
  ball(parent: THREE.Object3D, r: number, color: THREE.ColorRepresentation, x: number, y: number, z: number, surface: Surface = "plastic"): THREE.Mesh {
    const m = inked(this.geom("ball", () => new THREE.IcosahedronGeometry(1, 1)), this.mat(color, surface), 0.012);
    m.scale.setScalar(r);
    m.position.set(x, y, z);
    parent.add(m);
    return m;
  }

  /** A round rod of radius `r` from `a` to `b` (a post, a strut, a spoke). */
  rod(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, color: THREE.ColorRepresentation, surface: Surface = "metal", segs = 6): THREE.Mesh {
    const len = a.distanceTo(b);
    const m = new THREE.Mesh(this.geom(`rod:${segs}`, () => new THREE.CylinderGeometry(1, 1, 1, segs)), this.mat(color, surface, { roughness: 0.5 }));
    m.scale.set(r, len, r);
    m.castShadow = true;
    m.position.copy(a).add(b).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(UP, b.clone().sub(a).normalize());
    parent.add(m);
    return m;
  }

  /**
   * A striped cone (a tent roof, an umbrella, a carousel canopy): `n` wedges
   * alternating between the colors, its base `y` up and its tip `h` above.
   */
  stripedCone(parent: THREE.Object3D, r: number, h: number, y: number, n: number, colors: readonly THREE.ColorRepresentation[], surface: Surface = "cloth"): void {
    const step = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(new THREE.ConeGeometry(r, h, 2, 1, true, i * step, step), this.mat(colors[i % colors.length]!, surface, { side: THREE.DoubleSide }));
      m.castShadow = true;
      m.position.y = y + h / 2;
      parent.add(m);
    }
  }

  /** A striped drum (a tent's walls, a carousel's rim): `n` upright panels alternating between the colors. */
  stripedDrum(parent: THREE.Object3D, r: number, h: number, y: number, n: number, colors: readonly THREE.ColorRepresentation[], surface: Surface = "cloth"): void {
    const step = (Math.PI * 2) / n;
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 2, 1, true, i * step, step), this.mat(colors[i % colors.length]!, surface, { side: THREE.DoubleSide }));
      m.castShadow = m.receiveShadow = true;
      m.position.y = y + h / 2;
      parent.add(m);
    }
  }
}

/** Points along a path every `step` metres, through a smooth curve, with the way it runs at each (`prims/curve.ts`, shared with the sim, #231). */
export function samplePath(points: readonly THREE.Vector3[], step: number): { at: THREE.Vector3; dir: THREE.Vector3 }[] {
  return sampleCurve(points, step).map(({ at, dir }) => ({ at: new THREE.Vector3(at.x, at.y, at.z), dir: new THREE.Vector3(dir.x, dir.y, dir.z).normalize() }));
}

/** The sideways direction (level, to the right) of travel along `dir`, keeping `prev` where the path runs straight up or down. */
export function sideOf(dir: THREE.Vector3, prev?: THREE.Vector3): THREE.Vector3 {
  const side = new THREE.Vector3().crossVectors(dir, UP);
  if (side.lengthSq() < 1e-4) return prev?.clone() ?? new THREE.Vector3(1, 0, 0);
  side.normalize();
  if (prev && side.dot(prev) < 0) side.negate();
  return side;
}
